import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import { Job } from '../types/Job';
import { formatAmount as fmt } from '../utils/formatAmount';

interface Props {
  jobs: Job[];
  todayCount?: number;
}


const JobQueueCard: React.FC<Props> = ({ jobs, todayCount = 0 }) => {
  const pending = jobs.filter(j => j.status === 'pending');
  const assigned = jobs.filter(j => j.status === 'assigned');
  // Pre-completion jobs carry amount 0 (revenue is recognized at completion), so
  // the live queue reports negotiated pipeline value from intakeAmount.
  const totalValue = jobs.reduce((sum, j) => sum + j.intakeAmount, 0);
  const pendingValue = pending.reduce((sum, j) => sum + j.intakeAmount, 0);
  const assignedValue = assigned.reduce((sum, j) => sum + j.intakeAmount, 0);

  return (
    <View style={styles.wrapper}>
      <LinearGradient
        colors={['#063669', '#274e82']}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={styles.card}
      >
        <View style={styles.heroRow}>
          <View style={styles.heroLeft}>
            <Text style={styles.heroLabel}>Jobs in Queue</Text>
            <Text style={styles.heroAmount}>{fmt(totalValue)}</Text>
          </View>
          <View style={styles.badge}>
            <Text style={styles.badgeCount}>{jobs.length} active · {todayCount} today</Text>
          </View>
        </View>

        <View style={styles.tilesRow}>
          <View style={styles.tile}>
            <Text style={styles.tileLabel}>Pending</Text>
            <Text style={styles.tileCount}>{pending.length} jobs</Text>
            <Text style={styles.tileAmount}>{fmt(pendingValue)}</Text>
          </View>
          <View style={[styles.tile, styles.assignedTile]}>
            <Text style={styles.tileLabel}>Assigned</Text>
            <Text style={[styles.tileCount, styles.assignedText]}>{assigned.length} jobs</Text>
            <Text style={[styles.tileAmount, styles.assignedText]}>{fmt(assignedValue)}</Text>
          </View>
        </View>
      </LinearGradient>
    </View>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    paddingHorizontal: 20,
  },
  card: {
    borderRadius: 6,
    marginBottom: 12,
    padding: 20,
    shadowColor: '#111c2d',
    shadowOffset: { width: 0, height: 12 },
    shadowOpacity: 0.06,
    shadowRadius: 40,
    elevation: 4,
  },
  heroRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  heroLeft: {
    flex: 1,
  },
  heroLabel: {
    fontFamily: 'Inter-Regular',
    fontSize: 11,
    color: 'rgba(255,255,255,0.7)',
    letterSpacing: 1.2,
    textTransform: 'uppercase',
    marginBottom: 6,
  },
  heroAmount: {
    fontFamily: 'Manrope-Bold',
    fontSize: 34,
    color: 'white',
  },
  badge: {
    backgroundColor: 'rgba(255,255,255,0.15)',
    borderRadius: 20,
    paddingHorizontal: 12,
    paddingVertical: 6,
    alignItems: 'center',
  },
  badgeCount: {
    fontFamily: 'Manrope-Bold',
    fontSize: 18,
    color: 'white',
    lineHeight: 22,
  },
  tilesRow: {
    flexDirection: 'row',
    gap: 10,
  },
  tile: {
    flex: 1,
    backgroundColor: 'rgba(255,255,255,0.1)',
    borderRadius: 6,
    padding: 14,
  },
  assignedTile: {
    borderWidth: 1,
    borderColor: 'rgba(100,220,180,0.35)',
  },
  tileLabel: {
    fontFamily: 'Inter-Regular',
    fontSize: 10,
    color: 'rgba(255,255,255,0.75)',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 4,
  },
  tileCount: {
    fontFamily: 'Manrope-SemiBold',
    fontSize: 16,
    color: 'white',
    lineHeight: 20,
  },
  tileAmount: {
    fontFamily: 'Inter-Regular',
    fontSize: 12,
    color: 'rgba(255,255,255,0.8)',
    marginTop: 2,
  },
  assignedText: {
    color: '#7eefd0',
  },
});

export default JobQueueCard;
